import { CartPromoField } from '../promotions/CartPromoField'
import { promoCartSignature, type AppliedPromo } from '../promotions/promotionModel'
import { formatPayLabel, payForOrders } from '../../lib/paystack'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { styled } from '@linaria/react'
import { minimalVars } from '@ovasabi/ui-minimal/tokens'
import {
  MinimalButton,
  MinimalAlert,
  MinimalCard,
  MinimalDropdown,
  MinimalEmptyState,
  MinimalInput,
  type MinimalOption,
} from '@ovasabi/ui-minimal'
import { compareTimestamps, type TenantProjectionStore } from '@ovasabi/frontend-kit'
import {
  Money,
  PageHeader,
  SchedulePicker,
  Screen,
  StatusBadge,
} from '../../components/chow'
import { BasketIcon, LifebuoyIcon } from '../../components/icons'
import {
  prototypeStoreRegistry,
  useCartItemSnapshot,
  useMarketplaceOrderSnapshot,
  useHelpTicketSnapshot,
} from '../../generated/prototypeRuntime'
import { allowSeedContent, commandPayload, commands, isLiveRuntime } from '../../lib/runtime'
import { formatScheduled } from '../../lib/scheduling'
import { MAX_BASKET_ORDERS, clearReservationBasket, removeReservationBasketIds, useReservationBasketOrders } from './reservationBasket'
import { enumShort } from './chefShared'
import { useBackNav, useChooseChow } from './ChooseChowContext'
import {
  clearCartLines,
  customerCartRecords,
  dropCartLines,
  emitCartError,
  restoreCartLines,
  setCartLineQuantity,
} from './cart'
import { useCartWrites } from './cartWrites'
import { useChefOpenMap, useKitchenNames } from './projections'
import {
  emptyQuote,
  parseCartQuote,
  prototypeQuote,
  type CartQuote,
} from './cartQuote'
import { displayTitle } from '../../lib/displayName'
type Store = TenantProjectionStore<Record<string, unknown>>

const getStore = (key: string): Store | undefined =>
  prototypeStoreRegistry.get(key) as unknown as Store | undefined

export { InboxPage } from './InboxPage'

const OrderList = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: ${minimalVars.space.xs};
`


/* ── Help: open a support ticket + track past tickets ────────────────── */

const HELP_TOPICS: readonly MinimalOption<string>[] = [
  { value: 'order', label: 'Order issue' },
  { value: 'payment', label: 'Payment or wallet' },
  { value: 'delivery', label: 'Delivery or rider' },
  { value: 'account', label: 'Account' },
  { value: 'other', label: 'Something else' },
]

const HelpStyle = {
  Form: styled.form`
    display: grid;
    gap: ${minimalVars.space.xs};
  `,
  Textarea: styled.textarea`
    width: 100%;
    box-sizing: border-box;
    min-height: 108px;
    resize: vertical;
    padding: 10px 12px;
    border: 1px solid ${minimalVars.color.borderStrong};
    border-radius: ${minimalVars.radius.md};
    background: ${minimalVars.color.bgSurface};
    color: ${minimalVars.color.textPrimary};
    font: inherit;
    font-size: ${minimalVars.typography.bodySize};
    line-height: 1.5;
    &::placeholder {
      color: ${minimalVars.color.textTertiary};
    }
    &:focus-visible {
      outline: 2px solid ${minimalVars.color.brand};
      outline-offset: 1px;
      border-color: ${minimalVars.color.brand};
    }
  `,
  Label: styled.span`
    color: ${minimalVars.color.textSecondary};
    font-size: ${minimalVars.typography.captionSize};
    font-weight: ${minimalVars.typography.weightSemibold};
  `,
  Field: styled.label`
    display: grid;
    gap: 6px;
  `,
  SectionTitle: styled.h2`
    margin: ${minimalVars.space.sm} 0 0;
    font-family: ${minimalVars.typography.displayFamily};
    font-weight: ${minimalVars.typography.weightSemibold};
    /* 16.8px sits 4.8% from bodySize and 24% from h2Size. A heading cannot
       take bodySize, so this stays fixed. */
    font-size: 1.05rem;
    color: ${minimalVars.color.textPrimary};
  `,
  Ticket: styled.div`
    display: grid;
    gap: 4px;
  `,
  TicketTop: styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: ${minimalVars.space.xs};
  `,
  Subject: styled.span`
    font-weight: ${minimalVars.typography.weightSemibold};
    color: ${minimalVars.color.textPrimary};
  `,
  Body: styled.p`
    margin: 0;
    color: ${minimalVars.color.textSecondary};
    font-size: ${minimalVars.typography.captionSize};
    line-height: 1.45;
  `,
  Meta: styled.span`
    color: ${minimalVars.color.textTertiary};
    font-size: ${minimalVars.typography.metaSize};
    text-transform: capitalize;
  `,
}

const helpTicketStatus = (raw: string): 'open' | 'busy' | 'closed' => {
  switch (raw) {
    case 'resolved':
    case 'closed':
      return 'closed'
    case 'escalated':
    case 'pending':
      return 'busy'
    default:
      return 'open'
  }
}

export const HelpPage = () => {
  const back = useBackNav()
  const { profileId } = useChooseChow()
  const [topic, setTopic] = useState<string>('order')
  const [subject, setSubject] = useState('')
  const [body, setBody] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [submitNotice, setSubmitNotice] = useState<'sent' | 'error' | ''>('')

  const tickets = useHelpTicketSnapshot() as { records: readonly Record<string, unknown>[] }
  const myTickets = useMemo(() => {
    const mine = tickets.records.filter((t) =>
      isLiveRuntime() ? String(t.profile_id ?? '') === profileId : true,
    )
    return mine
      .slice()
      .sort((a, b) => compareTimestamps(a.created_at, b.created_at, String(a.id ?? ''), String(b.id ?? ''), { direction: 'desc' }))
  }, [tickets.records, profileId])

  const canSubmit = subject.trim().length > 0 && body.trim().length > 0 && !submitting

  const submit = async (event: React.FormEvent) => {
    event.preventDefault()
    if (!canSubmit) return
    setSubmitting(true)
    setSubmitNotice('')

    const topicLabel = HELP_TOPICS.find((t) => t.value === topic)?.label ?? 'Support'
    const payload = {
      profile_id: profileId,
      channel: 'in_app',
      subject: `${topicLabel}: ${subject.trim()}`,
      body: body.trim(),
    }

    // Emit the foundation command (intent). Durable truth is committed
    // server-side and flows back as an assist.help_tickets projection.
    try {
      if (isLiveRuntime()) {
        await commands.assist.createHelpTicket(payload)
      } else {
        void commands.assist.createHelpTicket(payload).catch(() => undefined)
        getStore('assist.help_tickets')?.upsert({
          id: `ticket-${Math.random().toString(36).slice(2)}`,
          profile_id: profileId,
          channel: topic,
          subject: `${topicLabel}: ${subject.trim()}`,
          body: body.trim(),
          status: 'open',
          created_at: new Date().toISOString(),
        })
      }
      setSubject('')
      setBody('')
      setTopic('order')
      setSubmitNotice('sent')
    } catch {
      setSubmitNotice('error')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Screen header={<PageHeader title="Help" accentDot onBack={back} />}>
      {submitNotice === 'sent' ? (
        <MinimalAlert tone="success" title="Your request is with support">
          You can track replies and status changes below.
        </MinimalAlert>
      ) : submitNotice === 'error' ? (
        <MinimalAlert tone="danger" title="Your request was not sent">
          Check your connection, then try again. Your message is still in the form.
        </MinimalAlert>
      ) : null}
      <MinimalCard padding="md">
        <HelpStyle.Form onSubmit={submit}>
          <HelpStyle.Field as="div">
            <HelpStyle.Label>Topic</HelpStyle.Label>
            <MinimalDropdown
              options={HELP_TOPICS}
              value={topic}
              onChange={setTopic}
              aria-label="Help topic"
            />
          </HelpStyle.Field>
          <MinimalInput
            label="Subject"
            placeholder="Briefly, what's happening?"
            value={subject}
            maxLength={120}
            onChange={(e) => setSubject(e.target.value)}
          />
          <HelpStyle.Field>
            <HelpStyle.Label>Message</HelpStyle.Label>
            <HelpStyle.Textarea
              placeholder="Tell us the details — order reference, what went wrong, and what you'd like us to do."
              value={body}
              maxLength={2000}
              onChange={(e) => setBody(e.target.value)}
            />
          </HelpStyle.Field>
          <MinimalButton type="submit" disabled={!canSubmit}>
            {submitting ? 'Sending…' : 'Send to support'}
          </MinimalButton>
        </HelpStyle.Form>
      </MinimalCard>

      <HelpStyle.SectionTitle>Your tickets</HelpStyle.SectionTitle>
      {myTickets.length === 0 ? (
        <MinimalEmptyState
          icon={<LifebuoyIcon />}
          title="No support requests"
          description="When you contact support, updates and replies will be kept here for you."
        />
      ) : (
        <OrderList>
          {myTickets.map((ticket) => (
            <MinimalCard key={String(ticket.id)} padding="sm" enter={false}>
              <HelpStyle.Ticket>
                <HelpStyle.TicketTop>
                  <HelpStyle.Subject>{String(ticket.subject ?? 'Support request')}</HelpStyle.Subject>
                  <StatusBadge status={helpTicketStatus(enumShort(ticket.status, 'open'))} />
                </HelpStyle.TicketTop>
                <HelpStyle.Body>{String(ticket.body ?? '')}</HelpStyle.Body>
                <HelpStyle.Meta>
                  {String(ticket.channel ?? 'support')} ·{' '}
                  {ticket.created_at
                    ? new Date(String(ticket.created_at)).toLocaleString(undefined, {
                        month: 'short',
                        day: 'numeric',
                        hour: 'numeric',
                        minute: '2-digit',
                      })
                    : 'just now'}
                </HelpStyle.Meta>
              </HelpStyle.Ticket>
            </MinimalCard>
          ))}
        </OrderList>
      )}
    </Screen>
  )
}

/* ── Cart: review → checkout ─────────────────────────────────────────── */

const CartStyle = {
  DeliveryFields: styled.fieldset`
    display: grid;
    gap: ${minimalVars.space.xs};
    min-width: 0;
    margin: 0;
    padding: 0;
    border: 0;
  `,
  Heading: styled.h2`
    margin: 0;
    font-size: ${minimalVars.typography.bodySize};
    font-weight: ${minimalVars.typography.weightSemibold};
  `,
  Details: styled.details`
    min-width: 0;
    summary {
      display: flex;
      align-items: center;
      min-height: 44px;
      cursor: pointer;
      color: ${minimalVars.color.textSecondary};
      font-size: ${minimalVars.typography.captionSize};
    }
    summary::after { content: '+'; margin-left: auto; }
    &[open] > summary::after { content: '−'; }
    > div { margin-top: ${minimalVars.space.xs}; }
    summary { gap: ${minimalVars.space.xs}; line-height: 1.5; }
    summary::after { flex-shrink: 0; }
  `,
  List: styled.div`
    display: grid;
    grid-template-columns: minmax(0, 1fr);
    gap: ${minimalVars.space.xs};
  `,
  Row: styled.div`
    display: grid;
    grid-template-columns: minmax(0, 1fr);
    align-items: center;
    gap: ${minimalVars.space.xs};
    flex-wrap: wrap;
    padding-block: ${minimalVars.space.xs};
    border-bottom: 1px solid ${minimalVars.color.borderSubtle};
    @media (min-width: 640px) { grid-template-columns: minmax(0, 1fr) auto; }
  `,
  LineActions: styled.div`
    display: flex;
    align-items: center;
    gap: ${minimalVars.space['2xs']};
    flex-wrap: wrap;
    > :first-child { margin-right: auto; }
    justify-content: flex-end;
    > button { min-height: 44px; min-width: 44px; padding-inline: 10px; }
  `,
  Copy: styled.div`
    display: grid;
    gap: 2px;
    min-width: 0;
    flex: 1 1 120px;
  `,
  Name: styled.span`
    overflow-wrap: anywhere;
    font-weight: ${minimalVars.typography.weightSemibold};
    color: ${minimalVars.color.textPrimary};
  `,
  Meta: styled.span`
    color: ${minimalVars.color.textTertiary};
    font-size: ${minimalVars.typography.captionSize};
  `,
  /* Quantity stepper: the line's only control. "Remove" as a separate link
     could not be reached on a narrow screen once the price sat beside it, and
     it made changing a quantity a remove-then-re-add errand. Stepping down
     from 1 removes the line. */
  Stepper: styled.div`
    display: flex;
    align-items: center;
    gap: 2px;
    border: 1px solid ${minimalVars.color.borderSubtle};
    border-radius: ${minimalVars.radius.pill};
    padding: 2px;
    background: ${minimalVars.color.bgSurface};
  `,
  Step: styled(MinimalButton)`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 44px;
    height: 44px;
    border: 0;
    border-radius: 50%;
    background: transparent;
    color: ${minimalVars.color.brand};
    cursor: pointer;
    font: inherit;
    /* The glyph centres in a fixed 44px target. No type token fits a control
       mark, so the 16.8px stays fixed. */
    font-size: 1.05rem;
    line-height: 1;
    transition: background 120ms ease, opacity 120ms ease;

    &:hover:not(:disabled) {
      background: ${minimalVars.color.brandSoft};
    }
    &:disabled {
      color: ${minimalVars.color.textTertiary};
      cursor: not-allowed;
    }
  `,
  Qty: styled.span`
    min-width: 22px;
    text-align: center;
    font-weight: ${minimalVars.typography.weightSemibold};
    color: ${minimalVars.color.textPrimary};
    font-variant-numeric: tabular-nums;
  `,
  BreakdownRow: styled.div`
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    gap: ${minimalVars.space.sm};
    > :last-child { flex-shrink: 0; }
    color: ${minimalVars.color.textSecondary};
    font-size: ${minimalVars.typography.captionSize};
  `,
  Summary: styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding-top: ${minimalVars.space.xs};
    border-top: 1px solid ${minimalVars.color.borderSubtle};
    font-weight: ${minimalVars.typography.weightSemibold};
  `,
  Kitchen: styled.div`
    display: grid;
    gap: ${minimalVars.space.xs};
  `,
  KitchenHead: styled.div`
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: ${minimalVars.space.xs};
    flex-wrap: wrap;
  `,
  KitchenName: styled.span`
    font-weight: ${minimalVars.typography.weightSemibold};
    color: ${minimalVars.color.textPrimary};
  `,
  SubTotalRow: styled.div`
    display: flex;
    justify-content: space-between;
    font-weight: ${minimalVars.typography.weightSemibold};
    color: ${minimalVars.color.textPrimary};
    font-size: ${minimalVars.typography.captionSize};
    padding-top: ${minimalVars.space['2xs']};
    border-top: 1px dashed ${minimalVars.color.borderSubtle};
  `,
  Page: styled.div`
    display: grid;
    gap: ${minimalVars.space.sm};
    min-width: 0;
  `,
  KitchenBlock: styled.div`
    display: grid;
    gap: ${minimalVars.space.xs};
    margin-top: ${minimalVars.space.xs};
    padding: ${minimalVars.space.sm};
    border-radius: ${minimalVars.radius.md};
    background: ${minimalVars.color.bgSurface};
    border: 1px solid ${minimalVars.color.borderSubtle};
  `,

}

/** Mint a fresh reference for a locally-echoed order (dummy/offline checkout). */

/**
 * Undo a checkout that created orders but never got paid for.
 *
 * Each uncharged order is cancelled first, so a restored cart line cannot become
 * a second order for the same food. The line then goes back to the cart, so the
 * customer retries from a full cart instead of an empty one.
 *
 * `unpaidOrders` maps merchant id to the order id created for it, and holds
 * only orders whose charge did not clear.
 */
const unwindUnpaidOrders = async (
  unpaidOrders: ReadonlyMap<string, string>,
  items: readonly Record<string, unknown>[],
  profileId: string,
): Promise<void> => {
  for (const orderId of unpaidOrders.values()) {
    try {
      await commands.marketplace.setOrderStatus(orderId, {
        status: 'cancelled',
        actor_profile_id: profileId,
        reason: 'checkout:payment_not_completed',
      })
    } catch (err) {
      console.error('Failed to cancel an unpaid order:', err)
    }
  }
  const owed = new Set(unpaidOrders.keys())
  await restoreCartLines(
    items.filter((i) => owed.has(String(i.merchant_profile_id ?? ''))),
    profileId,
  )
}

/**
 * Ask the server what the cart costs.
 *
 * The cart used to price itself from a local copy of the delivery table, the
 * plan table, and the service fee rules. That copy was free to drift from the
 * code that actually charged the customer, and when it did the cart screen and
 * the order disagreed. Now the server states the price once and both read it.
 *
 * The previous quote is kept on a failed refresh rather than cleared, so a
 * transient failure cannot make a full cart look free. An empty cart resolves
 * immediately.
 */
/** The server's price for the cart, plus whether it is safe to check out yet. */
interface CartQuoteState {
  quote: CartQuote
  /** A quote is in flight, so the bills on screen may not match the cart. */
  pending: boolean
  /** The last quote failed, so nothing here can be trusted to price checkout. */
  failed: boolean
  retry: () => void
}

const useCartQuote = (
  items: readonly Record<string, unknown>[],
  profileId: string,
  deliveryChoice: string,
): CartQuoteState => {
  const [quote, setQuote] = useState<CartQuote>(emptyQuote)
  const [pending, setPending] = useState(false)
  const [failed, setFailed] = useState(false)
  const [quoteSignature, setQuoteSignature] = useState('')
  const [refresh, setRefresh] = useState(0)
  const live = isLiveRuntime() || !allowSeedContent()
  const writes = useCartWrites()
  /* Keyed by what the server actually prices, not by array identity. The cart
     projection hands out a fresh array on every render, so depending on `items`
     would re-quote on every render and never settle. */
  const signature = useMemo(
    () =>
      `${deliveryChoice}|${items
        .map((item) => `${String(item.id ?? '')}:${Number(item.quantity ?? 0)}:${Number(item.unit_amount_minor ?? 0)}:${String(item.item_name ?? '')}`)
        .sort()
        .join('|')}`,
    [items, deliveryChoice],
  )
  // Read inside the effect without being a dependency, for the same reason.
  const latestItems = useRef(items)
  latestItems.current = items

  useEffect(() => {
    const lines = latestItems.current
    if (lines.length === 0) {
      setQuote(emptyQuote)
      setPending(false)
      setFailed(false)
      return
    }
    if (!live) {
      setQuote(prototypeQuote(lines, deliveryChoice))
      setPending(false)
      setFailed(false)
      return
    }
    let active = true
    setPending(true)
    setFailed(false)
    if (writes.pending > 0) return
    void commands.marketplace
      .quoteCart({ customer_profile_id: profileId, delivery_choice: deliveryChoice })
      .then((response) => {
        if (!active) return
        setQuote(parseCartQuote(commandPayload(response), latestItems.current))
        setQuoteSignature(signature)
        setFailed(false)
      })
      .catch((err) => {
        if (!active) return
        // The last good quote is kept. Showing a zero total would be worse than
        // a briefly stale one, because a zero reads as "free" — but checkout
        // must be blocked, since the bills it walks come from here.
        console.error('Cart quote failed:', err)
        setFailed(true)
      })
      .finally(() => {
        if (active) setPending(false)
      })
    return () => {
      active = false
    }
  }, [signature, live, profileId, deliveryChoice, writes.pending, writes.revision, refresh])

  return { quote, pending: writes.pending > 0 || pending || (live && !failed && items.length > 0 && quoteSignature !== signature), failed, retry: () => setRefresh((value) => value + 1) }
}

export const CartPage = () => {
  const [promos, setPromos] = useState<Record<string, AppliedPromo | undefined>>({})
  const navigate = useNavigate()
  const back = useBackNav()
  const { profileId } = useChooseChow()
  const [scheduledAt, setScheduledAt] = useState('')
  const [placingOrder, setPlacingOrder] = useState(false)
  const submittingPayment = useRef(false)
  const [paymentError, setPaymentError] = useState('')
  const deliveryAddress = ''
  const deliveryChoice = 'self_pickup'
  const deliveryNotes = ''
  const cart = useCartItemSnapshot() as { records: readonly Record<string, unknown>[] }

  const items = useMemo(() => {
    return customerCartRecords(cart.records, profileId, isLiveRuntime())
  }, [cart.records, profileId])

  const orderSnapshot = useMarketplaceOrderSnapshot() as { records: readonly Record<string, unknown>[] }
  const planned = useReservationBasketOrders(profileId, orderSnapshot.records)
  const plannedBalance = planned.orders.reduce((sum, order) => sum + Math.max(0, Number(order.total_minor ?? 0) - Number(order.amount_paid_minor ?? 0)), 0)
  const hasBasket = items.length > 0 || planned.orders.length > 0 || planned.pending || planned.failed
  const kitchenName = useKitchenNames()

  /* Kitchens that are closed right now still take *scheduled* orders, but not
     "order now". If the cart holds items from a closed kitchen, checkout is
     allowed only once the customer has picked a time to schedule. */
  const openMap = useChefOpenMap()
  const closedItemNames = useMemo(() => {
    const names = new Set<string>()
    for (const item of items) {
      const mid = String(item.merchant_profile_id ?? '')
      if (mid && openMap.get(mid) === false) names.add(String(item.item_name ?? 'a dish'))
    }
    return [...names]
  }, [items, openMap])
  const hasClosedKitchen = closedItemNames.length > 0
  const mustSchedule = hasClosedKitchen && !scheduledAt
  
  /* Checkout is per kitchen: each chef's lines become their own order with its own
     fees, and the whole basket is then paid in ONE transaction. The bills below
     are the SERVER's, from `marketplace:quote_cart`, which prices each kitchen
     with the same function checkout calls. They used to be recomputed here in a
     second implementation, which is how the cart and the order disagreed about
     what the food cost.

     A promo code is applied on top, per kitchen, and stays visible as its own
     line. It is quoted separately by `marketplace:quote_promo`, so it is not
     part of this quote. */
  const { quote, pending: quotePending, failed: quoteFailed, retry: retryQuote } = useCartQuote(items, profileId, deliveryChoice)
  const bills = quote.bills
  const quotedLineIds = new Set(bills.flatMap((bill) => bill.items.map((item) => String(item.id))))
  const unquotedItems = items.filter((item) => !quotedLineIds.has(String(item.id)))
  const { subtotalMinor, serviceFeeMinor, totalMinor: originalTotalMinor, itemCount } =
    quote.totals
  const fallbackItemCount = items.reduce((sum, item) => sum + Number(item.quantity ?? 1), 0) || items.length
  const fallbackSubtotalMinor = items.reduce(
    (sum, item) => sum + Number(item.quantity ?? 1) * Number(item.unit_amount_minor ?? 0),
    0,
  )
  const displayItemCount = quoteFailed ? fallbackItemCount : itemCount
  const displaySubtotalMinor = quoteFailed ? fallbackSubtotalMinor : subtotalMinor

  /* The bills drive which orders checkout creates and in which order, so a cart
     must not be checked out against a quote that has not landed or that failed.
     Without this the loop would walk an empty list and place no orders at all,
     which reads to the customer as a silent no-op. */
  const quotingNow = quotePending || quoteFailed

  const validPromos = Object.fromEntries(bills.map((bill) => {
    const promo = promos[bill.merchantId]
    return [bill.merchantId, promo?.signature === promoCartSignature(bill) ? promo : undefined]
  })) as Record<string, AppliedPromo | undefined>
  const discountMinor = Object.values(validPromos).reduce((sum, promo) => sum + (promo?.discountMinor ?? 0), 0)
  const totalMinor = originalTotalMinor - discountMinor + plannedBalance

  /* One line's quantity write. `busyLineId` disables that line's stepper while
     the command is in flight: the command sets an ABSOLUTE quantity, so two
     clicks racing on the same line would both send the same "current + 1" and
     the second would land as a no-op. */
  const [busyLineId, setBusyLineId] = useState('')

  const stepItem = async (item: Record<string, unknown>, delta: number) => {
    const id = String(item.id ?? '')
    if (!id || busyLineId) return
    setBusyLineId(id)
    try {
      await setCartLineQuantity(item, Number(item.quantity ?? 1) + delta, profileId)
    } finally {
      setBusyLineId('')
    }
  }

  const legacyDeliveryOrders = planned.orders.filter((order) => Number(order.delivery_fee_minor ?? 0) > 0)
  const checkoutBlocked = legacyDeliveryOrders.length > 0 || placingOrder || Boolean(busyLineId) || planned.pending || planned.failed ||
    (items.length > 0 && deliveryChoice !== 'self_pickup' && !deliveryAddress.trim()) || mustSchedule || quotingNow || totalMinor <= 0

  const submitPayment = async () => {
    if (submittingPayment.current || checkoutBlocked) return
    submittingPayment.current = true
    setPaymentError('')
    try { await handlePaymentSuccess() }
    catch (error) { setPaymentError(error instanceof Error ? error.message : 'Payment could not be verified. Check Orders before trying again.') }
    finally { submittingPayment.current = false }
  }

  const renderCartLine = (item: Record<string, unknown>) => {
    const lineId = String(item.id)
    const lineName = displayTitle(item.item_name)
    const lineQty = Math.max(1, Number(item.quantity ?? 1))
    const lineBusy = busyLineId === lineId
    return (
      <CartStyle.Row key={lineId}>
        <CartStyle.Copy>
          <CartStyle.Name>{lineName}</CartStyle.Name>
          <CartStyle.Meta><Money amount={Number(item.unit_amount_minor) / 100} size="sm" /> each</CartStyle.Meta>
        </CartStyle.Copy>
        <CartStyle.LineActions>
          <Money amount={(Number(item.unit_amount_minor) * lineQty) / 100} />
          {/* The stepper is the line's only control: stepping down from 1
              removes the line, so there is no second "Remove" to reach. */}
          <CartStyle.Stepper>
            <CartStyle.Step variant="quiet" type="button" disabled={lineBusy || placingOrder}
              aria-label={lineQty === 1 ? `Remove ${lineName} from your cart` : `One less ${lineName}`}
              onClick={() => void stepItem(item, -1)}>
              −
            </CartStyle.Step>
            <CartStyle.Qty aria-label={`Quantity of ${lineName}`}>{lineQty}</CartStyle.Qty>
            <CartStyle.Step variant="quiet" type="button" disabled={lineBusy || placingOrder}
              aria-label={`One more ${lineName}`} onClick={() => void stepItem(item, 1)}>
              +
            </CartStyle.Step>
          </CartStyle.Stepper>
        </CartStyle.LineActions>
      </CartStyle.Row>
    )
  }

  /* Lines grouped by kitchen, in the quote's bill order (the order checkout
     creates them in). A line the quote has not priced yet still shows, under
     its kitchen, so nothing in the cart ever disappears while pricing lands. */
  const kitchenGroups = useMemo(() => {
    const groups = new Map<string, Record<string, unknown>[]>()
    for (const bill of bills) groups.set(bill.merchantId, [])
    for (const item of items) {
      const id = String(item.merchant_profile_id ?? '')
      const group = groups.get(id)
      if (group) group.push(item)
      else groups.set(id, [item])
    }
    return [...groups.entries()]
      .filter(([, lines]) => lines.length > 0)
      .map(([merchantId, lines]) => ({ merchantId, lines, bill: bills.find((b) => b.merchantId === merchantId) }))
  }, [bills, items])

  const clearCart = () => {
    void clearCartLines(items, profileId)
    clearReservationBasket(profileId)
  }

  const handlePaymentSuccess = async () => {
    if (placingOrder || planned.pending || planned.failed || (items.length > 0 && deliveryChoice !== 'self_pickup' && !deliveryAddress.trim())) return
    setPlacingOrder(true)
    const cartStore = getStore('marketplace.carts')
    const orderStore = getStore('marketplace.marketplace_orders')
    if (!orderStore || (items.length > 0 && !cartStore) || !hasBasket) {
      setPlacingOrder(false)
      return
    }


    if (isLiveRuntime()) {
      /* The quote's bill order, not the cart's. A subscription delivery credit
         is single-use per customer, and the server spends it on the first bill
         in this list. Checking out in the same order is what makes the delivery
         line the customer was shown match the one they are charged. The
         customer still sees one checkout: these run in the background and are
         paid together below. */
      const merchantIds = bills.map((b) => b.merchantId).filter((id) => id.trim().length > 0)
      try {
        const unpaidOrders = new Map<string, string>()
        const orderIds: {
          orderId: string; merchantId: string; amountMinor: number
          optimisticOrder: Record<string, unknown>
        }[] = planned.orders.map((order) => ({
          orderId: String(order.id), merchantId: String(order.merchant_profile_id),
          amountMinor: Number(order.total_minor), optimisticOrder: order,
        }))
        if (orderIds.length + merchantIds.length > MAX_BASKET_ORDERS) throw new Error('A payment covers up to 96 orders. Remove some meals and try again.')
        const results: PromiseSettledResult<void>[] = []
        for (const merchantId of merchantIds) {
          try {
            const res = (await commands.marketplace.checkoutCart({
              cart_id: String(
                items.find((i) => String(i.merchant_profile_id) === merchantId)?.cart_id ?? 'cart-prototype',
              ),
              customer_profile_id: profileId,
              merchant_profile_id: merchantId,
              delivery_choice: deliveryChoice,
              promo_code: validPromos[merchantId]?.code,
              dropoff_address: [deliveryAddress.trim(), deliveryNotes.trim()].filter(Boolean).join(' — '),
              scheduled_at: scheduledAt || undefined,
            }))
            const order = commandPayload(res)
            const orderId = String(order.id ?? '')
            const amountMinor = Number(order.total_minor ?? order.subtotal_minor ?? 0)
            if (!orderId || amountMinor <= 0) throw new Error("We couldn’t get your order ready for payment.")
            // Recorded as owed, not paid. It only becomes paid once the charge clears.
            unpaidOrders.set(merchantId, orderId)
            const bill = bills.find((entry) => entry.merchantId === merchantId)!
            const confirmedTotal = Math.max(0, bill.totalMinor - (validPromos[merchantId]?.discountMinor ?? 0))
            if (amountMinor !== confirmedTotal) throw new Error('The price changed. Refresh your cart before payment.')
            const now = new Date().toISOString()
            const optimisticOrder = {
              ...order,
              id: orderId,
              customer_profile_id: String(order.customer_profile_id ?? profileId),
              merchant_profile_id: String(order.merchant_profile_id ?? merchantId),
              status: String(order.status ?? 'pending_payment'),
              delivery_choice: String(order.delivery_choice ?? 'chowdash'),
              dropoff_address: String(order.dropoff_address ?? [deliveryAddress.trim(), deliveryNotes.trim()].filter(Boolean).join(' — ')),
              scheduled_at: String(order.scheduled_at ?? scheduledAt ?? ''),
              created_at: String(order.created_at ?? now),
              updated_at: String(order.updated_at ?? now),
            }
            orderStore.upsert(optimisticOrder)
            // Created but NOT charged yet. Every kitchen's order is made first,
            // then the whole basket is paid in ONE transaction. Charging inside
            // this loop was why a three-kitchen cart opened three payment
            // windows.
            orderIds.push({ orderId, merchantId, amountMinor, optimisticOrder })
            results.push({ status: 'fulfilled', value: undefined })
          } catch (reason) {
            results.push({ status: 'rejected', reason })
            break
          }
        }

        const failures = results.filter((r) => r.status === 'rejected')
        if (failures.length > 0 || orderIds.length === 0) {
          // An order could not be created. Nothing was charged, so every line
          // that was turned into an order goes back to the cart and the customer
          // retries the whole basket.
          console.error('Checkout failed:', (failures[0] as PromiseRejectedResult | undefined)?.reason)
          await unwindUnpaidOrders(unpaidOrders, items, profileId)
          emitCartError(
            "Your order could not be prepared, so nothing was charged. Your cart is unchanged — please try again.",
            "Checkout didn't finish",
          )
          return
        }

        // One payment for the whole basket. The server prices the sum and
        // charges it once, then credits every kitchen's order from that single
        // capture. A scheduled order is charged now too: it used to skip the
        // charge while the modal still said Paystack was taking payment.
        try {
          await payForOrders(orderIds.map((entry) => entry.orderId), profileId)
        } catch (reason) {
          // One charge covers everything, so it either cleared or it did not.
          // There is no "some kitchens paid" state to reconcile.
          console.error('Payment failed:', reason)
          if (reason instanceof Error && reason.name === 'PaymentVerificationPendingError') {
            dropCartLines(items.map((item) => String(item.id)))
            removeReservationBasketIds(profileId, planned.orders.map((order) => String(order.id)))
            emitCartError('We’re still waiting to hear from Paystack. Your order is saved. Check Orders before you pay again.', 'Payment status pending')
            navigate('/orders')
            return
          }
          await unwindUnpaidOrders(unpaidOrders, items, profileId)
          emitCartError(
            'Your payment did not go through, so nothing was charged. Your cart is unchanged — please try again.',
            "Checkout didn't finish",
          )
          return
        }

        removeReservationBasketIds(profileId, planned.orders.map((order) => String(order.id)))
        // Paid: every order in the basket sheds its cart lines.
        for (const entry of orderIds) {
          unpaidOrders.delete(entry.merchantId)
          orderStore.upsert({
            ...entry.optimisticOrder,
            status: 'paid',
            amount_paid_minor: entry.amountMinor,
            updated_at: new Date().toISOString(),
          })
        }
        dropCartLines(
          items
            .filter((i) => orderIds.some((entry) => entry.merchantId === String(i.merchant_profile_id ?? '')))
            .map((i) => String(i.id)),
        )
        navigate('/orders')
      } catch (err) {
        console.error('Checkout failed:', err)
        emitCartError("We couldn't place your order. Please try again.", "Checkout didn't finish")
      } finally {
        setPlacingOrder(false)
      }
      return
    }

    setPlacingOrder(false)
    throw new Error('Connect to ChooseChow before checkout. No payment has been started.')
  }

  const kitchenCount = bills.length
  const savingsMinor = quote.totals.discountMinor + discountMinor
  const pricing = quotingNow || planned.pending || planned.failed
  const payLabel = placingOrder
    ? 'Waiting for payment verification…'
    : quotePending ? 'Getting your price…'
      : mustSchedule ? 'Pick a pickup time to continue'
        : pricing ? 'Price unavailable'
          : formatPayLabel(totalMinor)

  return (
    <Screen header={<PageHeader title="Cart" accentDot onBack={back} />}>
      {!hasBasket ? (
        <MinimalEmptyState
          icon={<BasketIcon />}
          title="Your cart is empty"
          description={planned.unavailable > 0
            ? 'Your selected meals are already paid, cancelled, or past their pickup time. Review your meal plan for current meals.'
            : 'To order from your meal plan, choose Add day to cart or Add week to cart there.'}
          action={<MinimalButton variant="secondary" onClick={() => navigate('/mealplan')}>View meal plan</MinimalButton>}
        />
      ) : (
        <CartStyle.Page>
          {planned.pending && <MinimalAlert tone="info" title="Loading planned meals">Checking your meals before checkout…</MinimalAlert>}
          {planned.failed && <MinimalAlert tone="danger" title="Could not load your planned meals" action={<MinimalButton size="sm" onClick={planned.retry} disabled={placingOrder}>Retry planned meals</MinimalButton>}>Your selections are saved. Retry to check your meals and balances. Nothing has been charged.</MinimalAlert>}
          {planned.unavailable > 0 && <MinimalAlert tone="info" title="Some planned meals are no longer payable">Paid, cancelled, or past meals are excluded from this checkout.</MinimalAlert>}

          {/* 1 · What you are buying, one block per kitchen. */}
          <MinimalCard padding="sm" enter={false}>
            <CartStyle.KitchenHead>
              <CartStyle.Heading>Your meals</CartStyle.Heading>
              <MinimalButton variant="ghost" size="sm" onClick={clearCart} disabled={placingOrder || Boolean(busyLineId)}>Clear cart</MinimalButton>
            </CartStyle.KitchenHead>
            <CartStyle.List aria-label="Your meals">
              {kitchenGroups.map(({ merchantId, lines, bill }) => {
                const promo = validPromos[merchantId]
                return (
                  <CartStyle.KitchenBlock key={merchantId}>
                    <CartStyle.KitchenHead>
                      <CartStyle.KitchenName>{kitchenName(merchantId)}</CartStyle.KitchenName>
                      {bill && <Money amount={Math.max(0, bill.totalMinor - (promo?.discountMinor ?? 0)) / 100} size="sm" tone="brand" />}
                    </CartStyle.KitchenHead>
                    <CartStyle.Meta>Pickup · {bill?.pickupAddress || 'You’ll see the pickup address once the kitchen accepts.'}</CartStyle.Meta>
                    {lines.map(renderCartLine)}
                    {bill && (
                      <CartPromoField
                        key={promoCartSignature(bill)}
                        bill={bill}
                        profileId={profileId}
                        applied={promo}
                        disabled={placingOrder || Boolean(busyLineId)}
                        onApply={(next) => setPromos((current) => ({ ...current, [merchantId]: next }))}
                      />
                    )}
                  </CartStyle.KitchenBlock>
                )
              })}
              {planned.orders.length > 0 && (
                <CartStyle.KitchenBlock>
                  <CartStyle.KitchenName>From your meal plan</CartStyle.KitchenName>
                  {planned.orders.map((order) => {
                    const paid = Number(order.amount_paid_minor ?? 0)
                    const delivery = Number(order.delivery_fee_minor ?? 0)
                    return (
                      <CartStyle.Row key={String(order.id)}>
                        <CartStyle.Copy>
                          <CartStyle.Name>{displayTitle(order.summary || order.item_name) || 'Meal details unavailable'}</CartStyle.Name>
                          <CartStyle.Meta>{kitchenName(String(order.merchant_profile_id))} · {formatScheduled(String(order.scheduled_at))}</CartStyle.Meta>
                          {delivery === 0 && <CartStyle.Meta>Kitchen pickup · {String(order.pickup_address || 'Message the kitchen to confirm the collection address.')}</CartStyle.Meta>}
                          {paid > 0 && <CartStyle.Meta>Already paid <Money amount={paid / 100} size="sm" /></CartStyle.Meta>}
                          {delivery > 0 && <MinimalAlert tone="warning" title="This meal needs a quick review">It was priced under an older plan, so checkout is paused for this basket. Contact support and we’ll sort it out.</MinimalAlert>}
                        </CartStyle.Copy>
                        <CartStyle.LineActions>
                          <Money amount={Math.max(0, Number(order.total_minor) - paid) / 100} />
                          <MinimalButton size="sm" variant="ghost" disabled={placingOrder} onClick={() => removeReservationBasketIds(profileId, [String(order.id)])}>Remove from cart</MinimalButton>
                        </CartStyle.LineActions>
                      </CartStyle.Row>
                    )
                  })}
                  <CartStyle.Meta>Removing a meal here keeps it in your meal plan. To cancel it, remove it from your meal plan.</CartStyle.Meta>
                </CartStyle.KitchenBlock>
              )}
            </CartStyle.List>
            {unquotedItems.length > 0 && <CartStyle.Meta>{quoteFailed ? 'Your dishes are saved. Retry pricing to confirm the total.' : 'Confirming your price…'}</CartStyle.Meta>}
          </MinimalCard>

          {/* 2 · When to collect. */}
          {items.length > 0 && (
            <MinimalCard padding="sm">
              <CartStyle.DeliveryFields disabled={placingOrder}>
                <CartStyle.Heading>Pickup time</CartStyle.Heading>
                {hasClosedKitchen && (
                  <MinimalAlert
                    tone={mustSchedule ? 'warning' : 'info'}
                    title={mustSchedule ? 'This kitchen is closed right now' : 'Scheduled for when the kitchen reopens'}
                  >
                    {mustSchedule
                      ? `${closedItemNames.join(', ')} — the kitchen isn’t taking orders now. Pick a time below to schedule your order for later.`
                      : 'Your order will be sent to the kitchen for the time you picked.'}
                  </MinimalAlert>
                )}
                <SchedulePicker value={scheduledAt} onChange={setScheduledAt} pickup={deliveryChoice === 'self_pickup'} />
              </CartStyle.DeliveryFields>
            </MinimalCard>
          )}

          {/* 3 · One summary, one payment. */}
          <MinimalCard padding="sm">
            <CartStyle.Kitchen>
              <CartStyle.Heading>Order summary</CartStyle.Heading>
              {items.length > 0 && <>
                <CartStyle.BreakdownRow>
                  <span>Food ({displayItemCount} item{displayItemCount === 1 ? '' : 's'})</span>
                  <Money amount={displaySubtotalMinor / 100} size="sm" />
                </CartStyle.BreakdownRow>
                <CartStyle.BreakdownRow>
                  <span>Service fee{kitchenCount > 1 ? ` (${kitchenCount} kitchens)` : ''}</span>
                  <Money amount={serviceFeeMinor / 100} size="sm" />
                </CartStyle.BreakdownRow>
              </>}
              {savingsMinor > 0 && <CartStyle.BreakdownRow><span>Savings</span><span>−<Money amount={savingsMinor / 100} size="sm" /></span></CartStyle.BreakdownRow>}
              {planned.orders.length > 0 && <CartStyle.BreakdownRow><span>Planned meals ({planned.orders.length})</span><Money amount={plannedBalance / 100} size="sm" /></CartStyle.BreakdownRow>}
              <CartStyle.Summary>
                <span>Total</span>
                {pricing
                  ? <CartStyle.Meta>{quoteFailed || planned.failed ? 'Price unavailable' : 'Confirming total…'}</CartStyle.Meta>
                  : <Money amount={totalMinor / 100} tone="brand" size="lg" />}
              </CartStyle.Summary>
              {quoteFailed && (
                <MinimalAlert tone="danger" title="We could not price your cart"
                  action={<MinimalButton size="sm" variant="secondary" onClick={retryQuote}>Retry pricing</MinimalButton>}>
                  Nothing has been charged.
                </MinimalAlert>
              )}
              {legacyDeliveryOrders.length > 0 && <MinimalButton variant="secondary" onClick={() => navigate('/help')}>Resolve previous payment</MinimalButton>}
              {paymentError && <MinimalAlert tone="danger" title="Checkout did not finish">{paymentError}</MinimalAlert>}
              <MinimalButton onClick={() => void submitPayment()} disabled={checkoutBlocked} loading={placingOrder}>
                {payLabel}
              </MinimalButton>
              <CartStyle.Meta>
                One secure Paystack payment covers {kitchenCount + (planned.orders.length > 0 ? 1 : 0) > 1 ? 'every kitchen' : 'your order'}.
                Each kitchen prepares its own order for pickup. After you pay, message the kitchen to agree a
                collection time or to send your own rider. Dish prices are set by each kitchen and include any VAT.
              </CartStyle.Meta>
            </CartStyle.Kitchen>
          </MinimalCard>
        </CartStyle.Page>
      )}
    </Screen>
  )
}
